<script setup>
import { computed } from 'vue'
import TrackTitle from '@/components/TrackTitle.vue'

/**
 * Track name with the link to the track page.
 *
 * The player name has UserName, the track name has this one: every page writes a track the same way
 * and gets the link for free. The names themselves are TrackTitle's (which of the two leads is the
 * site's choice, see composables/useTrackTitle.js).
 *
 * It is a link, so it cannot go inside another link: inside a `v-card :to` leave it out.
 */
const props = defineProps({
  /** Track id, as the server configures it (config/tracks.php) and the API returns it. */
  trackId: { type: String, default: null },
  /** Drop the flag where the name sits in a tight spot: a chip, a table header, a select. */
  flag: { type: Boolean, default: true },
  /** 'inline' keeps the two names on one line, 'stacked' puts the second one under the first. */
  variant: { type: String, default: 'inline' },
})

const to = computed(() => ({ name: 'track', params: { trackId: props.trackId } }))
</script>

<template>
  <RouterLink v-if="trackId" :to="to" class="track-name track-name--link">
    <TrackTitle :track-id="trackId" :variant="variant" :flag="flag" />
  </RouterLink>
  <span v-else class="track-name">–</span>
</template>

<style scoped>
/* As for the player name: the track keeps the colour of the context and only shows itself as a link
   on hover, so a list of tracks stays readable instead of becoming a wall of blue. */
.track-name {
  color: inherit;
  text-decoration: none;
}
.track-name--link:hover {
  color: rgb(var(--v-theme-primary));
  text-decoration: underline;
}
</style>
